import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import StatusMessageForm from "../../home/toolbarMenu/StatusMessageForm";
import {
  hasScheduledStatusMessage,
  outOfOfficeVersion,
} from "../../home/toolbarMenu/statusMessageMappers";
import {
  selectExpertUserById,
  updateExpertOutOfOffice,
} from "../../user/expertUsersSlice";
import { ExpertOutOfOfficePeriod } from "../../user/types/entities";

type Props = {
  expertId: string | null;
  isOpen: boolean;
  close: () => void;
};

export const ExpertOutOfOfficeDialog = ({ expertId, isOpen, close }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const expert = useAppSelector((state) =>
    selectExpertUserById(state, expertId ?? ""),
  );

  const scheduledOutOfOffice = hasScheduledStatusMessage(
    expert?.account.outOfOffice,
  )
    ? expert?.account.outOfOffice
    : null;

  useEffect(() => {
    if (!isOpen) setIsLoading(false);
  }, [isOpen]);

  const handleSave = (outOfOffice: ExpertOutOfOfficePeriod) => {
    if (!expert) return;

    setIsLoading(true);
    dispatch(
      updateExpertOutOfOffice({
        expertId: expert.id,
        ...outOfOffice,
        version: outOfOfficeVersion(expert.account.outOfOffice),
      }),
    ).then((action) => {
      setIsLoading(false);
      if (isFulfilled(action)) close();
    });
  };

  const handleClear = () => {
    if (!expert) return;

    setIsLoading(true);
    dispatch(
      updateExpertOutOfOffice({
        expertId: expert.id,
        version: outOfOfficeVersion(expert.account.outOfOffice),
        message: null,
        from: null,
        until: null,
      }),
    ).then((action) => {
      setIsLoading(false);
      if (isFulfilled(action)) close();
    });
  };

  return (
    <DialogComponent
      autoWidth
      open={isOpen}
      onClose={close}
      disableBackdropClick={false}
    >
      {expert ? (
        <StatusMessageForm
          key={expert.id}
          disableDatePickerPortal={false}
          isLoading={isLoading}
          onCancel={close}
          onClear={scheduledOutOfOffice ? handleClear : undefined}
          onSave={handleSave}
          outOfOffice={scheduledOutOfOffice}
          title={t("settings:editStatus")}
        />
      ) : null}
    </DialogComponent>
  );
};
